import { ThemeProvider } from '@alphonsocode/kallos';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import { FirstRow } from './firstRow';
import { SecondRow } from './secondRow';
import CarouselComponent from '../Carousel';
import { useLazyFetchAssetsListQuery } from 'services/api/creativePreview';

type Asset = {
  fileName: string;
  path: string;
  label?: string;
  placeholder?: {
    fileName: string;
    path: string;
  };
};

type CardProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  testType: string;
  mosaicUrl?: string | null;
  mosaicStagingUrl?: string | null;
  expandAllArtifacts: boolean;
  artifactsCollapseNonce: number;
  /** Row matches Mosaic URL alphCrid / alphCid — open visual assets by default. */
  mosaicAutoExpand?: boolean;
};

export function Card({
  data,
  testType,
  mosaicUrl,
  mosaicStagingUrl,
  expandAllArtifacts,
  artifactsCollapseNonce,
  mosaicAutoExpand = false,
}: CardProps) {
  const testResultId = String(data?._id ?? '');
  const initialAssets = Array.isArray(data?.assets) ? data.assets : [];
  const [fetchAssetsList] = useLazyFetchAssetsListQuery();

  const [mediaItems, setMediaItems] = useState<
    {
      type: 'image' | 'video' | 'unknown';
      url: string;
      label: string;
      isLoading?: boolean;
      placeholderUrl?: string;
    }[]
  >([]);
  const [assetData, setAssetData] = useState<Asset[]>(initialAssets);
  const [assetsLoadedForTestResultId, setAssetsLoadedForTestResultId] =
    useState<string | null>(initialAssets.length > 0 ? testResultId : null);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsError, setAssetsError] = useState(false);
  const pendingAssetsRequestRef = useRef<Promise<Asset[]> | null>(null);
  const testResultIdRef = useRef(testResultId);
  testResultIdRef.current = testResultId;
  /** When set to testResultId, a full load (including empty) finished for this row. */
  const [artifactsLoadedForTestResultId, setArtifactsLoadedForTestResultId] =
    useState<string | null>(null);
  const [localArtifactsExpanded, setLocalArtifactsExpanded] = useState(false);
  const [expandAllCollapsedByUser, setExpandAllCollapsedByUser] =
    useState(false);
  const [mosaicCollapsedByUser, setMosaicCollapsedByUser] = useState(false);

  useEffect(() => {
    setMediaItems(prev => {
      prev.forEach(item => {
        if (item?.url?.startsWith('blob:')) {
          URL.revokeObjectURL(item.url);
        }
      });
      return [];
    });
    setArtifactsLoadedForTestResultId(null);
    setAssetData(initialAssets);
    setAssetsLoadedForTestResultId(
      initialAssets.length > 0 ? testResultId : null,
    );
    setExpandAllCollapsedByUser(false);
    setAssetsLoading(false);
    setAssetsError(false);
    pendingAssetsRequestRef.current = null;
  }, [testResultId]);

  const handleTestResultArtifactsLoaded = useCallback((id: string) => {
    setArtifactsLoadedForTestResultId(id);
  }, []);

  const ensureAssetsLoaded = useCallback(async () => {
    if (assetsLoadedForTestResultId === testResultId) {
      return assetData;
    }

    if (pendingAssetsRequestRef.current) {
      return pendingAssetsRequestRef.current;
    }

    const s3Prefix = data?.s3Prefix;
    const adSlot = data?.offsetAdSlot ?? data?.adSlot;

    if (data?.status === 'Unsupported Test' || !s3Prefix || !adSlot) {
      setAssetData([]);
      setAssetsLoadedForTestResultId(testResultId);
      return [];
    }

    setAssetsLoading(true);
    setAssetsError(false);

    const requestTestResultId = testResultId;
    const request = fetchAssetsList({ s3Prefix, adSlot, testType }, true)
      .unwrap()
      .then(response => {
        const assets = Array.isArray(response?.assets) ? response.assets : [];
        if (testResultIdRef.current === requestTestResultId) {
          setAssetData(assets);
          setAssetsLoadedForTestResultId(requestTestResultId);
        }
        return assets;
      })
      .catch(error => {
        console.error('Failed to lazy-load creative assets:', error);
        if (testResultIdRef.current === requestTestResultId) {
          setAssetData([]);
          setAssetsLoadedForTestResultId(requestTestResultId);
          setAssetsError(true);
        }
        return [];
      })
      .finally(() => {
        if (testResultIdRef.current === requestTestResultId) {
          pendingAssetsRequestRef.current = null;
          setAssetsLoading(false);
        }
      });

    pendingAssetsRequestRef.current = request;
    return request;
  }, [
    assetData,
    assetsLoadedForTestResultId,
    data?.adSlot,
    data?.offsetAdSlot,
    data?.s3Prefix,
    data?.status,
    fetchAssetsList,
    testResultId,
  ]);

  useEffect(() => {
    setLocalArtifactsExpanded(false);
    setExpandAllCollapsedByUser(false);
    setMosaicCollapsedByUser(false);
  }, [artifactsCollapseNonce]);

  useEffect(() => {
    setMosaicCollapsedByUser(false);
  }, [mosaicAutoExpand]);

  useEffect(() => {
    if (!expandAllArtifacts) {
      setExpandAllCollapsedByUser(false);
    }
  }, [expandAllArtifacts]);

  const artifactsVisible =
    (expandAllArtifacts && !expandAllCollapsedByUser) ||
    localArtifactsExpanded ||
    (mosaicAutoExpand && !mosaicCollapsedByUser);

  useEffect(() => {
    if (artifactsVisible) {
      ensureAssetsLoaded();
    }
  }, [artifactsVisible, ensureAssetsLoaded]);

  const assetsLoaded = assetsLoadedForTestResultId === testResultId;
  const dataWithAssets = {
    ...data,
    assets: assetData,
  };

  const handleScreenshotMediaToggle = () => {
    if (artifactsVisible) {
      setLocalArtifactsExpanded(false);
      if (expandAllArtifacts) {
        setExpandAllCollapsedByUser(true);
      }
      if (mosaicAutoExpand) {
        setMosaicCollapsedByUser(true);
      }
    } else {
      setExpandAllCollapsedByUser(false);
      setMosaicCollapsedByUser(false);
      setLocalArtifactsExpanded(true);
    }
  };

  return (
    <ThemeProvider defaultMode="light">
      <div
        className="card"
        style={{
          width: '100%',
          height: 'auto',
          minHeight: 0,
          marginTop: 12,
          marginBottom: 12,
          backgroundColor: '#f0f6f7',
          border: '1px solid #d6e4e8',
          borderRadius: 6,
          boxShadow: 'none',
        }}
      >
        <div className="card-body" style={{ padding: '14px 18px 16px' }}>
          <div
            className="container-fluid"
            style={{ paddingLeft: 0, paddingRight: 0 }}
          >
            <FirstRow
              data={data}
              testType={testType}
              mosaicUrl={mosaicUrl}
              mosaicStagingUrl={mosaicStagingUrl}
            />
            <SecondRow
              data={dataWithAssets}
              mediaItems={mediaItems}
              testType={testType}
              artifactsVisible={artifactsVisible}
              assetsLoaded={assetsLoaded}
              assetsLoading={assetsLoading}
              ensureAssetsLoaded={ensureAssetsLoaded}
            />
            <div style={{ marginTop: 4, paddingLeft: 8, paddingRight: 8 }}>
              <button
                type="button"
                onClick={handleScreenshotMediaToggle}
                className="d-flex align-items-center justify-content-between w-100"
                style={{
                  padding: '7px 14px',
                  border: '1px solid #b8d4d9',
                  borderRadius: 8,
                  backgroundColor: '#e8f2f4',
                  cursor: 'pointer',
                  fontFamily: 'lg-smart, sans-serif',
                  fontWeight: 600,
                  fontSize: 13,
                  color: '#006e7d',
                  textAlign: 'left',
                }}
                aria-expanded={artifactsVisible}
              >
                <span>User Experience Visual Assets</span>
                {artifactsVisible ? (
                  <MdExpandLess size={22} aria-hidden />
                ) : (
                  <MdExpandMore size={22} aria-hidden />
                )}
              </button>
              {artifactsVisible ? (
                <CarouselComponent
                  data={dataWithAssets}
                  assetData={assetData}
                  assetsLoaded={assetsLoaded}
                  assetsLoading={assetsLoading}
                  assetsError={assetsError}
                  ensureAssetsLoaded={ensureAssetsLoaded}
                  setMediaItems={setMediaItems}
                  mediaItems={mediaItems}
                  testResultId={testResultId}
                  artifactsLoadedForTestResultId={
                    artifactsLoadedForTestResultId
                  }
                  onTestResultArtifactsLoaded={handleTestResultArtifactsLoaded}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
}
